/**
 * chartResponsive — Reglas puras de adaptación de gráficas al tamaño disponible.
 *
 * QUÉ HACE: decide cuántas etiquetas mostrar en los ejes (horas, categorías y
 *   valores), el radio/grosor de las líneas, el modo de la leyenda, la escala
 *   térmica secuencial y la agrupación Top N + "Otros". Son funciones puras:
 *   reciben el tamaño medido y devuelven valores listos para Recharts.
 * QUÉ INVOCA: formatChartAxisCop (formateador compacto de moneda) y MOTION
 *   (duraciones/easing del sistema de animación).
 * QUÉ EXPORTA: constantes de escalas, la animación nativa de Recharts y las
 *   funciones de resolución (ticks, leyenda, líneas, color térmico y calor) y
 *   toRechartsInterval.
 * QUÉ NO DEBE HACER: no toca el DOM ni React; no conoce datos de negocio.
 */
import { formatChartAxisCop } from "@/lib/utils/formatters";
import { MOTION } from "@/lib/animation/motion";

/** Altura (px) por debajo de la cual el eje Y muestra solo 2 niveles. */
export const CHART_Y_SMALL_HEIGHT = 150;

/**
 * Niveles de referencia del eje Y: **3** en tarjetas normales ($0, $600k,
 * $1.2M) y 2 solo en contenedores muy bajos (< 150 px).
 */
export function resolveYAxisTickCount(height: number): number {
  return height > 0 && height < CHART_Y_SMALL_HEIGHT ? 2 : 3;
}

/** Pasos simétricos válidos para 24 horas (siempre incluyen medianoche y mediodía). */
export const CHART_HOUR_TICK_STEPS = [1, 2, 3, 4, 6, 12] as const;

/**
 * Paso de etiquetas de hora (1, 2, 3, 4, 6 u 8) por ancho del contenedor:
 * monitores grandes → cada hora; laptops → cada 2; tarjetas medianas → cada 3-4
 * (12AM, 4AM, 8AM, 12PM, 4PM, 8PM); tarjetas estrechas → cada 6 u 8
 * (12AM, 6AM, 12PM, 6PM / 12AM, 8AM, 4PM) para que no se peguen. Mismo
 * resultado en Horas Pico y Evolución.
 */
export function resolveHourTickStep(
  count: number,
  availableWidth: number,
): number {
  if (count <= 0) return 1;
  if (availableWidth <= 0) return count > 16 ? 4 : 1;
  if (availableWidth >= 1100) return 1;
  if (availableWidth >= 720) return 2;
  if (availableWidth >= 500) return 3;
  if (availableWidth >= 360) return 4;
  if (availableWidth >= 240) return 6;
  return 8;
}

/**
 * Paso de etiquetas para ejes categóricos genéricos (días, meses, métodos…):
 * cada etiqueta debe caber en `minLabelWidth` px.
 */
export function resolveCategoryTickStep(
  count: number,
  availableWidth: number,
  minLabelWidth = 40,
): number {
  if (count <= 0) return 1;
  if (availableWidth <= 0) return count > 16 ? 3 : count > 8 ? 2 : 1;
  return Math.max(1, Math.ceil((count * minLabelWidth) / availableWidth));
}

/**
 * Paso de etiquetas del eje de filas de una matriz (24 horas en vertical). Se
 * basa en el **alto real de cada fila**, no en el total: con pocas filas (5
 * métodos de pago) se muestran todas; con 24 horas se salta solo cuando la fila
 * es muy baja. Con **12 filas (los 12 meses)** se muestrea cada 3 (ene, abr,
 * jul, oct) para que el margen izquierdo no se sature: las 12 filas de bloques
 * térmicos se mantienen intactas, solo se ocultan las etiquetas intermedias.
 */
export function resolveRowLabelStep(
  count: number,
  availableHeight: number,
): number {
  if (count <= 0) return 1;
  if (count === 12) return 3;
  if (count <= 12) return 1;
  if (availableHeight <= 0) return count > 16 ? 4 : 1;
  const rowHeight = availableHeight / count;
  if (rowHeight >= 16) return 1;
  if (rowHeight >= 10) return 2;
  if (rowHeight >= 6) return 4;
  return 6;
}

/**
 * Convierte un paso de etiquetas al prop `interval` de Recharts: `interval`
 * indica cuántas etiquetas saltar (0 = todas, 1 = una de cada dos, …).
 */
export function toRechartsInterval(step: number): number {
  return Math.max(0, Math.round(step) - 1);
}

/** Granularidad temporal del eje X (compartida por todas las gráficas). */
export type XAxisGranularity =
  | "HOURS"
  | "DAYS"
  | "WEEK"
  | "QUINCENAS"
  | "MONTHS"
  | "YEARS"
  | "DECADES";

/** Configuración del eje X (prop `interval` de Recharts). */
export interface XAxisConfig {
  interval: number | "preserveStartEnd";
}

/**
 * Configuración ÚNICA del eje X según la granularidad y el ancho medido. Todas
 * las gráficas temporales deben consumirla para no truncar ni amontonar:
 *   - HOURS: paso simétrico (1/2/3/4/6/12) → p. ej. cada 4 h (12AM, 4AM, 8AM…).
 *   - QUINCENAS: solo hay 2 buckets → todas las etiquetas.
 *   - MONTHS/DAYS/YEARS/DECADES: paso por ancho de etiqueta (nunca se fusionan).
 * Con ancho 0 (sin medir todavía) devuelve un paso conservador.
 */
export function getXAxisConfig(
  granularity: XAxisGranularity,
  width: number,
  count: number,
): XAxisConfig {
  if (count <= 1) return { interval: 0 };
  switch (granularity) {
    case "HOURS":
      return {
        interval: toRechartsInterval(resolveHourTickStep(count, width)),
      };
    case "QUINCENAS":
      return { interval: 0 };
    case "MONTHS":
      return {
        interval: toRechartsInterval(resolveCategoryTickStep(count, width, 48)),
      };
    case "DAYS":
    case "WEEK":
      return {
        interval: toRechartsInterval(resolveCategoryTickStep(count, width, 40)),
      };
    case "YEARS":
    case "DECADES":
    default:
      return {
        interval: toRechartsInterval(resolveCategoryTickStep(count, width, 30)),
      };
  }
}

/** Mapea el nivel de drill del motor temporal a la granularidad del eje X. */
export function drillLevelToGranularity(level: string): XAxisGranularity {
  switch (level) {
    case "hours":
      return "HOURS";
    case "quincenas":
      return "QUINCENAS";
    case "months":
      return "MONTHS";
    case "years":
      return "YEARS";
    case "decades":
      return "DECADES";
    case "week":
      return "WEEK";
    case "days":
    default:
      return "DAYS";
  }
}

/** Política de nodos de una línea: radio, cada cuántos puntos y solo el pico. */
export interface ChartLineDotPolicy {
  /** Radio del nodo (0 = sin nodos salvo el pico si `peakOnly`). */
  radius: number;
  /** Dibuja un nodo cada `step` puntos. */
  step: number;
  /** En contenedores pequeños, dibuja únicamente el nodo del pico. */
  peakOnly: boolean;
}

/**
 * Nodos de línea por ancho: grandes → todos con radio 3.5; medianos → radio 2
 * cada 3 puntos; pequeños → sin nodos salvo **el pico resaltado**.
 */
export function resolveLineDotPolicy(width: number): ChartLineDotPolicy {
  if (width >= 600) return { radius: 3.5, step: 1, peakOnly: false };
  if (width >= 350) return { radius: 2, step: 3, peakOnly: false };
  return { radius: 0, step: 1, peakOnly: true };
}

/** Grosor de línea según el ancho del contenedor. */
export function resolveLineStrokeWidth(width: number): number {
  return width >= 600 ? 2.5 : 1.8;
}

/** Modo de la leyenda: completa, abreviada (3 letras) o inicial. */
export type ChartLegendMode = "full" | "short" | "initial";

/** Hasta esta longitud la etiqueta se considera corta y nunca se abrevia. */
const LEGEND_SHORT_LABEL_MAX = 8;

/** A partir de esta longitud la etiqueta es "realmente larga" (admite inicial). */
const LEGEND_LONG_LABEL_MIN = 14;

/**
 * Elige el modo de leyenda. Las **frases cortas** ("Caja", "Web", "Gastos",
 * "Ventas"…) se muestran completas siempre, sin importar el ancho; solo las
 * etiquetas largas se abrevian a 3 letras (`Pres.`) cuando no caben y, si son
 * realmente largas y el espacio es mínimo, a su inicial. Si no se conocen las
 * etiquetas se usa solo el ancho disponible por serie.
 */
export function resolveLegendMode(
  availableWidth: number,
  seriesCount: number,
  labels: readonly string[] = [],
): ChartLegendMode {
  if (seriesCount <= 0) return "full";
  const perSeries = availableWidth / seriesCount;
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);

  if (longest > 0 && longest <= LEGEND_SHORT_LABEL_MAX) return "full";
  if (perSeries >= 110) return "full";
  if (perSeries >= 64) return "short";
  return longest >= LEGEND_LONG_LABEL_MIN ? "initial" : "short";
}

/** Abrevia la etiqueta de una serie según el modo de leyenda. */
export function abbreviateLegendLabel(
  label: string,
  mode: ChartLegendMode,
): string {
  if (mode === "full") return label;
  if (mode === "short") {
    return label.length <= 4 ? label : label.slice(0, 3);
  }
  return label.slice(0, 1).toUpperCase();
}

/**
 * Modo de la leyenda de series extra: prioriza el nombre completo si el ancho
 * total estimado de las etiquetas cabe en una fila; si no, abrevia (3 letras) o
 * inicial. Más fiel que `resolveLegendMode` cuando hay muchas series cortas
 * (p. ej. 5 métodos de pago), donde el reparto por serie sobreestima el ancho.
 */
export function resolveExtraLegendMode(
  width: number,
  labels: readonly string[],
): ChartLegendMode {
  if (labels.length === 0) return "full";
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  if (longest <= LEGEND_SHORT_LABEL_MAX) return "full";
  if (width <= 0) return "full";
  const estimated =
    labels.reduce((sum, label) => sum + label.length, 0) * 6.2 +
    labels.length * 24;
  if (width >= estimated) return "full";
  if (width >= estimated * 0.62) return "short";
  return longest >= LEGEND_LONG_LABEL_MIN ? "initial" : "short";
}

/**
 * Formateador listo para el `formatter` del `Legend` de Recharts: abrevia las
 * etiquetas de las series según el modo calculado por `resolveLegendMode`.
 */
export function resolveLegendFormatter(
  mode: ChartLegendMode,
): (value: string) => string {
  return (value: string) => abbreviateLegendLabel(value, mode);
}

/**
 * Tipografía compartida de las tarjetas de gráfico. Escala con el **ancho de la
 * tarjeta** (container queries), no con el viewport: en tarjetas estrechas el
 * título baja a 11 px y crece hasta 18 px en tarjetas anchas. Requiere que la
 * tarjeta tenga la clase `@container`.
 */
export const CHART_TITLE_CLASS =
  "text-[11px] font-bold leading-tight @[420px]:text-[12px] @[560px]:text-[13px] @[700px]:text-[15px] @[900px]:text-lg";

/** Subtítulo descriptivo de una tarjeta de gráfico (11 px, tono gris). */
export const CHART_SUBTITLE_CLASS = "text-[11px] font-medium leading-snug";

/**
 * Hint de drill de las gráficas (encabezado y pickers). Escala con el ancho de
 * la tarjeta igual que el título, pero un punto menor para no robar protagonismo
 * en tarjetas estrechas (donde el título mide 11 px).
 */
export const CHART_HINT_CLASS =
  "text-[10px] leading-snug @[420px]:text-[11px] @[700px]:text-xs";

/**
 * Botón compacto de cabecera (selector de vista y menú ⋮). Escala con el ancho
 * de la tarjeta: 28 px en tarjetas estrechas y 32 px desde 520 px.
 */
export const CHART_ICON_BUTTON_CLASS =
  "inline-flex h-7 min-h-7 min-w-7 shrink-0 items-center justify-center rounded-lg transition-colors @[520px]:h-8 @[520px]:min-h-8 @[520px]:min-w-8";

/**
 * Tipo de curva de líneas y áreas (`type` de Recharts): **recta / zigzag**
 * (`linear`), no suave (`monotone`). Las curvas suaves insinúan valores
 * intermedios que no existen entre dos puntos; la recta muestra la fluctuación
 * real punto a punto. Regla visual global: todas las gráficas deben consumir
 * esta constante en vez de hardcodear `"monotone"`/`"linear"`.
 */
export const CHART_LINE_CURVE = "linear" as const;

/**
 * Animación nativa de Recharts (entrada y cambio de datos): duración y easing
 * centralizados del sistema (`lib/animation/motion.ts`). Se aplica con spread
 * (`{...RECHARTS_ANIMATION}`) sobre `Bar`, `Line`, `Area`, `Pie`, `Radar` y
 * `Scatter`. Es la animación **optimizada** de Recharts: no re-renderiza el
 * árbol React en cada frame (a diferencia de interpolar los datos a mano).
 */
export const RECHARTS_ANIMATION = {
  animationDuration: MOTION.series,
  animationEasing: "ease-out",
} as const;

/** Escala térmica verde (métodos de pago): clara → verde institucional. */
export const CHART_THERMAL_GREEN = [
  "#d1fae5",
  "#a7f3d0",
  "#6ee7b7",
  "#34d399",
  "#10b981",
  "#059669",
] as const;

/** Escala térmica roja (gastos): clara → rojo intenso. */
export const CHART_THERMAL_RED = [
  "#fee2e2",
  "#fecaca",
  "#fca5a5",
  "#f87171",
  "#ef4444",
  "#dc2626",
] as const;

/** Escala térmica secuencial compartida (claro → oscuro = menor → mayor). */
export const CHART_THERMAL_PALETTE = [
  "#dbeafe",
  "#93c5fd",
  "#60a5fa",
  "#3b82f6",
  "#1d4ed8",
  "#1e3a8a",
] as const;

/** Color térmico para un valor normalizado (0 = menor, 1 = mayor). */
export function resolveThermalColor(
  ratio: number,
  palette: readonly string[] = CHART_THERMAL_PALETTE,
): string {
  const clamped = Math.min(1, Math.max(0, ratio));
  const index = Math.round(clamped * (palette.length - 1));
  return palette[index] ?? palette[0];
}

/** Escala de calor discreta (5 niveles) compartida por heatmaps y calendario. */
export const CHART_HEAT_LEVEL_CLASSES = [
  "bg-gray-100 dark:bg-slate-700/50 text-gray-400 dark:text-slate-500",
  "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200",
  "bg-emerald-300 dark:bg-emerald-700/70 text-emerald-900 dark:text-emerald-100",
  "bg-emerald-500 text-white",
  "bg-emerald-700 text-white",
] as const;

/** Nivel de calor (0 = vacío, 4 = máximo) de un valor dentro de una escala. */
export function resolveHeatLevel(value: number, maxValue: number): number {
  if (maxValue <= 0 || value <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((value / maxValue) * 4)));
}

/** Formateador compacto de moneda para los ejes (k/M/B, sin decimales largos). */
export function formatChartAxisValue(value: number): string {
  return formatChartAxisCop(value);
}

/**
 * ¿Hay algún dato real que dibujar? Devuelve `false` cuando todas las filas
 * están vacías o todos sus valores (para las claves dadas) son 0. Es el
 * criterio único para decidir el estado vacío de un gráfico (`ChartEmptyState`),
 * de modo que "sin filas" y "filas en cero" se traten igual en todas las
 * tarjetas. Los valores negativos cuentan como dato.
 */
export function hasChartValues<T extends object>(
  rows: readonly T[],
  keys: readonly string[],
): boolean {
  if (rows.length === 0 || keys.length === 0) return false;
  return rows.some((row) => {
    const record = row as Record<string, unknown>;
    return keys.some((key) => Number(record[key] ?? 0) !== 0);
  });
}
